// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Read-only JSON with syntax highlighting (requirement 3.4).
import { Highlight, themes, type PrismTheme } from 'prism-react-renderer';
import { useColorMode } from '../lib/colorMode';

/**
 * The stock github theme's property (#36acaa) and string (#e3116c) colors fail WCAG AA contrast on its
 * #f6f8fa background (found by axe in the task 12.1 end-to-end run). These colors are all at least 4.5:1.
 */
export const jsonTheme: PrismTheme = {
  plain: themes.github.plain,
  styles: [
    { types: ['comment', 'prolog', 'doctype', 'cdata'], style: { color: '#57606a', fontStyle: 'italic' } },
    { types: ['string', 'attr-value'], style: { color: '#0a3069' } },
    { types: ['punctuation', 'operator'], style: { color: '#393a34' } },
    {
      types: ['property', 'number', 'boolean', 'constant', 'variable', 'symbol', 'entity', 'url', 'regex'],
      style: { color: '#0550ae' },
    },
    {
      types: ['keyword', 'atrule', 'attr-name', 'selector', 'tag', 'function', 'deleted'],
      style: { color: '#cf222e' },
    },
  ],
};

/** Dark mode: GitHub's dark palette, every color at least 4.5:1 on its #0d1117 background. */
export const jsonThemeDark: PrismTheme = {
  plain: { color: '#e6edf3', backgroundColor: '#0d1117' },
  styles: [
    { types: ['comment', 'prolog', 'doctype', 'cdata'], style: { color: '#8b949e', fontStyle: 'italic' } },
    { types: ['string', 'attr-value'], style: { color: '#a5d6ff' } },
    { types: ['punctuation', 'operator'], style: { color: '#c9d1d9' } },
    {
      types: ['property', 'number', 'boolean', 'constant', 'variable', 'symbol', 'entity', 'url', 'regex'],
      style: { color: '#79c0ff' },
    },
    {
      types: ['keyword', 'atrule', 'attr-name', 'selector', 'tag', 'function', 'deleted'],
      style: { color: '#ff7b72' },
    },
  ],
};

export function JsonPreview({ value, label = 'Blueprint schema JSON' }: { value: unknown; label?: string }) {
  const code = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  const dark = useColorMode() === 'dark';
  return (
    <Highlight code={code} language="json" theme={dark ? jsonThemeDark : jsonTheme}>
      {({ style, tokens, getLineProps, getTokenProps }) => (
        <pre
          aria-label={label}
          tabIndex={0}
          style={{ ...style, padding: 12, borderRadius: 8, overflow: 'auto', maxHeight: 520, fontSize: 13, margin: 0 }}
        >
          {tokens.map((line, i) => (
            <div key={i} {...getLineProps({ line })}>
              {line.map((token, k) => (
                <span key={k} {...getTokenProps({ token })} />
              ))}
            </div>
          ))}
        </pre>
      )}
    </Highlight>
  );
}
